import { useState } from 'react'
import { Avatar } from '@/components/ui/Avatar'
import { formatTime } from '@/lib/utils/time'
import { colorFromUserId } from '@/lib/utils/avatar'
import { stripReplyFallback } from '@/lib/utils/message'
import { MediaMessage } from './MediaMessage'
import { FormattedText } from './FormattedText'
import { LinkPreview } from './LinkPreview'
import { ReplyPreview } from './ReplyPreview'
import { ReactionBar } from './ReactionBar'
import { ReadReceipts, type Reader } from './ReadReceipts'
import { MessageActions } from './MessageActions'
import { MapDialog } from '@/components/ui/MapDialog'
import { ForwardDialog } from './ForwardDialog'
import { MessageInfoModal } from './MessageInfoModal'
import { UserProfileModal } from '@/components/members/UserProfileModal'
import type { SerializedEvent } from '@/lib/matrix/events'

interface MessageBubbleProps {
  event: SerializedEvent
  isOwn: boolean
  isGrouped: boolean
  roomId: string
  onReply: (event: SerializedEvent) => void
  onEdit: (event: SerializedEvent) => void
  onReact: (event: SerializedEvent) => void
  onOpenThread?: (event: SerializedEvent) => void
  onJumpToEvent?: (eventId: string) => void
  aiEnabled?: boolean
  readers?: Reader[]
}

interface ThreadSummary {
  count: number
  current_user_participated?: boolean
}

export function MessageBubble({ event, isOwn, isGrouped, roomId, onReply, onEdit, onReact, onOpenThread, onJumpToEvent, aiEnabled, readers }: MessageBubbleProps) {
  const [mapCoords, setMapCoords] = useState<import('@/lib/utils/location').GeoCoords | null>(null)
  const [showProfile, setShowProfile] = useState(false)
  const [showForward, setShowForward] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const senderColor = colorFromUserId(event.sender)
  const senderName = event.senderName
  const msgtype = event.content.msgtype as string | undefined
  const isSticker = event.type === 'm.sticker'
  const isLocation = msgtype === 'm.location'
  const isMedia = isSticker || isLocation || msgtype === 'm.image' || msgtype === 'm.video' || msgtype === 'm.audio' || msgtype === 'm.file'
  const isEmote = msgtype === 'm.emote'
  const isNotice = msgtype === 'm.notice'
  const isEdited = !!(event.content['m.new_content'])
  const forwarded = event.content['io.chatpulse.forwarded']
  const forwardedFrom = forwarded && typeof forwarded === 'object' ? (forwarded as { from?: string }).from : undefined
  const isEncrypted = event.type === 'm.room.encrypted' && !msgtype
  const relatesTo = event.content['m.relates_to'] as Record<string, unknown> | undefined
  // Only treat as a reply preview when it's a plain reply, not a thread fallback.
  const replyToId =
    relatesTo?.rel_type !== 'm.thread'
      ? (relatesTo?.['m.in_reply_to'] as Record<string, unknown> | undefined)
      : undefined
  const rawBody = (event.content.body as string) ?? ''
  const body = replyToId ? stripReplyFallback(rawBody) : rawBody
  // Prefer the sender's rich HTML body when present (formatting, mention pills);
  // the renderer strips the rich-reply fallback itself.
  const html =
    event.content.format === 'org.matrix.custom.html'
      ? (event.content.formatted_body as string | undefined)
      : undefined

  // Server-bundled thread summary lives on the root event's unsigned relations.
  const threadSummary = (
    (event.unsigned?.['m.relations'] as Record<string, unknown> | undefined)?.['m.thread'] as
      | ThreadSummary
      | undefined
  )

  if (event.redacted) {
    return (
      <div className="msg-row">
        <div className="msg-gutter" />
        <div className="deleted-row">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Message deleted
        </div>
      </div>
    )
  }

  return (
    <div className="msg-row">
      <div className="msg-gutter">
        {!isGrouped ? (
          <button onClick={() => setShowProfile(true)} className="cursor-pointer">
            <Avatar mxc={event.senderAvatar} name={senderName} userId={event.sender} size={38} square />
          </button>
        ) : (
          <div className="msg-time font-data text-[rgb(var(--np-tx-muted))]">{formatTime(event.timestamp)}</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {!isGrouped && (
          <div className="flex items-baseline gap-2" style={{ marginBottom: 2 }}>
            <button
              onClick={() => setShowProfile(true)}
              className="font-semibold hover:underline cursor-pointer"
              style={{ fontSize: 13.5, color: isOwn ? 'rgb(var(--np-accent))' : senderColor }}
            >
              {senderName}
            </button>
            <span className="font-data text-[rgb(var(--np-tx-muted))]" style={{ fontSize: 10.5 }}>
              {formatTime(event.timestamp)}
            </span>
          </div>
        )}

        {!!forwarded && (
          <div className="flex items-center gap-1 text-[11px] italic text-[rgb(var(--np-tx-muted))]" style={{ marginBottom: 2 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 17 20 12 15 7" />
              <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
            </svg>
            {forwardedFrom ? `Forwarded from ${forwardedFrom}` : 'Forwarded'}
          </div>
        )}

        {replyToId && (
          <ReplyPreview
            roomId={roomId}
            replyToEventId={replyToId.event_id as string}
            onJumpTo={onJumpToEvent}
          />
        )}

        {isEncrypted ? (
          <p className="msg-body italic flex items-center gap-1.5 !text-[rgb(var(--np-tx-muted))]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Unable to decrypt message
          </p>
        ) : isMedia ? (
          <>
            <MediaMessage event={event} />
            {body && !isSticker && !isLocation && body !== event.content.filename && !/^\S+\.\w{2,4}$/.test(body) && (
              <p className="msg-body mt-1.5">
                <FormattedText text={body} html={html} roomId={roomId} />
              </p>
            )}
          </>
        ) : isEmote ? (
          <p className="msg-body italic">
            <span className="not-italic font-medium" style={{ color: isOwn ? 'rgb(var(--np-accent))' : senderColor }}>
              {senderName}
            </span>{' '}
            <FormattedText text={body} html={html} roomId={roomId} />
          </p>
        ) : (
          <>
            <p className={`msg-body ${isNotice ? '!text-[rgb(var(--np-tx-muted))]' : ''}`}>
              <FormattedText text={body} html={html} roomId={roomId} />
            </p>
            <LinkPreview text={body} />
          </>
        )}

        {isEdited && (
          <span className="text-[10px] text-[rgb(var(--np-tx-muted))] ml-1">(edited)</span>
        )}

        <ReactionBar event={event} roomId={roomId} />

        {threadSummary && threadSummary.count > 0 && onOpenThread && (
          <button
            onClick={() => onOpenThread(event)}
            className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-[rgb(var(--np-accent))] hover:underline"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {threadSummary.count} {threadSummary.count === 1 ? 'reply' : 'replies'}
          </button>
        )}

        {readers && readers.length > 0 && <ReadReceipts readers={readers} onClick={() => setShowInfo(true)} />}
      </div>

      <MessageActions
        event={event}
        roomId={roomId}
        aiEnabled={aiEnabled}
        onReply={() => onReply(event)}
        onEdit={() => onEdit(event)}
        onReact={() => onReact(event)}
        onForward={() => setShowForward(true)}
        onThread={onOpenThread ? () => onOpenThread(event) : undefined}
        onShowMap={(coords) => setMapCoords(coords)}
        onInfo={() => setShowInfo(true)}
      />
      {mapCoords && (
        <MapDialog coords={mapCoords} onClose={() => setMapCoords(null)} />
      )}
      {showForward && (
        <ForwardDialog event={event} onClose={() => setShowForward(false)} />
      )}
      {showInfo && (
        <MessageInfoModal event={event} roomId={roomId} onClose={() => setShowInfo(false)} />
      )}
      {showProfile && (
        <UserProfileModal userId={event.sender} roomId={roomId} onClose={() => setShowProfile(false)} />
      )}
    </div>
  )
}
